import React, { useRef, useState, useCallback, useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { SiteLink } from './Site';
import './not-found.css';

/* ── Microcopy ────────────────────────────────────────────────────────────── */
function getMicrocopy(count) {
  if (count === 0)   return { text: 'Nothing useful will happen.',                  big: false };
  if (count >= 10)   return { text: 'We are genuinely impressed. Still a 404 though.', big: true  };
  return               { text: 'Yep. Still a 404.',                                  big: true  };
}

/* ── rAF-based spin (cross-browser, axis-accurate) ────────────────────────── */
/*
  Uses the SVG `transform` attribute (rotate(angle)) rather than CSS
  `transform` + `transform-origin`, which Safari handles inconsistently
  for SVG <g> elements.  SVG rotate(angle) always pivots around (0,0) of
  the current coordinate system — which, for our viewBox="-66 -66 132 132",
  is the geometric centre of the viewport: exactly where the bearing sits.
*/
function rafSpin(el, fromDeg, toDeg, durationMs, rafRef, onDone) {
  if (rafRef.current) cancelAnimationFrame(rafRef.current);
  const startTime = performance.now();
  const delta     = toDeg - fromDeg;

  function frame(now) {
    const t     = Math.min((now - startTime) / durationMs, 1);
    const eased = 1 - Math.pow(1 - t, 3);          // cubic ease-out
    el.setAttribute('transform', `rotate(${fromDeg + delta * eased})`);
    if (t < 1) {
      rafRef.current = requestAnimationFrame(frame);
    } else {
      const normalised = ((toDeg % 360) + 360) % 360;  // keep in [0, 360)
      el.setAttribute('transform', `rotate(${normalised})`);
      rafRef.current = null;
      onDone(normalised);
    }
  }
  rafRef.current = requestAnimationFrame(frame);
}

/* ── SVG Fidget Spinner ───────────────────────────────────────────────────── */
/*
  Geometry (see rAF note above for why SVG transform is used):
    d = 30  — distance from SVG origin to each lobe centre
    r = 32  — lobe radius
    Lobe centres at 0° / 120° / 240° from 12-o-clock:
      Top:         ( 0, -30)
      Bottom-right:(26,  15)
      Bottom-left: (-26, 15)
    Inter-centre distance ≈ 52; 2r = 64 > 52, circles overlap → triangular hub. ✓
*/
function FidgetSpinner({ rotorRef, onSpin }) {
  return (
    <div
      className="nf-spinner-wrap"
      onClick={onSpin}
      role="presentation"
      aria-hidden="true"
      data-testid="nf-spinner-container"
    >
      <svg
        viewBox="-66 -66 132 132"
        className="nf-spinner-svg"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <filter id="nf-lobe-shadow" x="-35%" y="-30%" width="170%" height="170%">
            <feDropShadow dx="0" dy="4" stdDeviation="7"
              floodColor="#231208" floodOpacity=".16"/>
          </filter>
        </defs>

        {/* rotorRef on the <g> — SVG rotate() pivots around (0,0) = bearing centre */}
        <g ref={rotorRef}>
          <g className="nf-lobes" filter="url(#nf-lobe-shadow)">
            <circle cx="0"   cy="-30" r="32"/>
            <circle cx="26"  cy="15"  r="32"/>
            <circle cx="-26" cy="15"  r="32"/>
          </g>
          <circle r="19" className="nf-bearing-outer"/>
          <circle r="14" className="nf-bearing-inner"/>
          <circle r="7"  className="nf-bearing-well"/>
          <circle r="2"  className="nf-bearing-dot"/>
        </g>
      </svg>
    </div>
  );
}

/* ── NotFoundPage ─────────────────────────────────────────────────────────── */
export default function NotFoundPage() {
  const rotorRef    = useRef(null);   // SVG <g> element
  const spinningRef = useRef(false);  // synchronous gate
  const rotationRef = useRef(0);      // current normalised angle (0–360°)
  const animFrameRef = useRef(null);  // active rAF handle for cleanup

  const [spinning,  setSpinning]  = useState(false);
  const [spinCount, setSpinCount] = useState(0);

  useEffect(() => {
    return () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current); };
  }, []);

  const handleSpin = useCallback(() => {
    if (spinningRef.current || !rotorRef.current) return;

    const reduced  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const extra    = reduced ? 90 + Math.random() * 90 : 720 + Math.random() * 720 + Math.random() * 360;
    const newRot   = rotationRef.current + extra;
    const duration = reduced ? 350 : 1000 + Math.random() * 800;
    const fromRot  = rotationRef.current;

    spinningRef.current = true;
    setSpinning(true);
    setSpinCount(c => c + 1);

    rafSpin(rotorRef.current, fromRot, newRot, duration, animFrameRef, (normalised) => {
      rotationRef.current = normalised;
      spinningRef.current = false;
      setSpinning(false);
    });
  }, []);

  const { text: copyText, big: copyBig } = getMicrocopy(spinCount);

  return (
    <main id="main-content" className="nf-page" data-testid="not-found-page">
      <div className="nf-inner">

        {/* ── Hero copy ──────────────────────────────────────────── */}
        <header className="nf-header">
          <p className="nf-four-oh-four" aria-label="Error 404">404</p>
          <h1 className="nf-title">This is not a game.</h1>
          <p className="nf-sub">You landed somewhere that doesn&apos;t exist.</p>
          <p className="nf-since">Since you&apos;re here&hellip;</p>
        </header>

        {/* ── Fidget spinner ─────────────────────────────────────── */}
        <section className="nf-spinner-section" aria-label="Fidget spinner interaction">
          <FidgetSpinner rotorRef={rotorRef} onSpin={handleSpin}/>

          <button
            className="nf-press-btn"
            onClick={handleSpin}
            disabled={spinning}
            aria-label="Spin the fidget spinner"
            data-testid="nf-spin-button"
          >
            PRESS OR DO NOT PRESS
          </button>

          <p
            className={`nf-microcopy${copyBig ? ' nf-microcopy--big' : ''}`}
            aria-live="polite"
            aria-atomic="true"
            data-testid="nf-microcopy"
          >
            {copyText}
          </p>
        </section>

        {/* ── Navigation ─────────────────────────────────────────── */}
        <nav className="nf-nav" aria-label="Return to CubitPlay">
          <SiteLink href="/" className="nf-back-link" data-testid="nf-back-link">
            <ArrowLeft size={15} aria-hidden="true"/>
            Back to CubitPlay
          </SiteLink>
          <SiteLink href="/" className="nf-play-link" data-testid="nf-play-link">
            PLAY AN ACTUAL GAME
            <ArrowRight size={16} aria-hidden="true"/>
          </SiteLink>
        </nav>

      </div>
    </main>
  );
}
